import React from "react";

/**
 * Embed a Google Map for an institute. Uses lat/lng when provided (no API key
 * required via the public maps.google.com embed). Falls back to a textual
 * address query when coordinates are missing.
 *
 * Props:
 *   latitude, longitude  — string or number (decimal degrees)
 *   address              — fallback query string (e.g. "MIT, Aurangabad")
 *   title                — accessibility label
 *   className            — wrapper class (default 16:9 box)
 *   showDirections       — render a "Get directions" CTA below the map
 *   directionsLabel      — text on the CTA (default "Get directions")
 *   directionsClassName  — override CTA styling per site theme
 */
export default function InstituteMap({
  latitude,
  longitude,
  address = "",
  title = "Institute location",
  className = "w-full aspect-[16/9] rounded-sm overflow-hidden border border-black/10 bg-slate-100",
  testid = "institute-map",
  showDirections = true,
  directionsLabel = "Get directions",
  directionsClassName = "inline-flex items-center gap-2 mt-3 px-4 py-2 text-sm font-semibold bg-[#234333] hover:bg-[#1a3326] text-white rounded-sm transition-colors",
}) {
  const lat = (latitude ?? "").toString().trim();
  const lng = (longitude ?? "").toString().trim();
  const hasCoords = lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng));
  const query = hasCoords
    ? `${encodeURIComponent(lat)},${encodeURIComponent(lng)}`
    : encodeURIComponent((address || "").trim());

  if (!hasCoords && !address) return null;

  const src = `https://maps.google.com/maps?q=${query}&hl=en&z=15&output=embed`;
  // Google Maps directions URL — opens the Maps app on mobile, web on desktop.
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${query}`;
  return (
    <div data-testid={`${testid}-wrap`}>
      <div className={className} data-testid={testid}>
        <iframe
          title={title}
          src={src}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
      {showDirections && (
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={directionsClassName}
          data-testid={`${testid}-directions-btn`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="3 11 22 2 13 21 11 13 3 11" />
          </svg>
          {directionsLabel}
        </a>
      )}
    </div>
  );
}
